<script setup lang="ts">
import EmptyStateCard from './components/EmptyStateCard.vue'
import RewardTypeTag from './components/RewardTypeTag.vue'
import RewardUsageTag from './components/RewardUsageTag.vue'

const { t } = useI18n()
const { hasPermissionBySlug } = useAccess()

const { hasRewards, activeRewards, formattedRewards, loadRewards, pagination, navigateToRewardDetail } = useReward()

const isLoading = ref<boolean>(false)
const isRefreshing = ref<boolean>(false)

const pageTitle = computed(() => ({
  title: t('pages.reward.library.title')
}))

const canCreateReward = computed(() => hasPermissionBySlug('reward.create'))

const tableColumns = {
  image: {
    label: '',
    headerClass: 'w-20! text-left'
  },
  type: {
    label: t('pages.reward.table.type'),
    headerClass: 'w-48! text-left'
  },
  name: {
    label: t('pages.reward.table.name'),
    headerClass: 'w-64! text-left'
  },
  usage: {
    label: t('pages.reward.table.usage'),
    headerClass: 'w-auto! text-left'
  }
}

const tableRows = computed(() => {
  if (!formattedRewards.value || formattedRewards.value.length === 0) {
    return []
  }

  return formattedRewards.value.map((reward) => ({
    id: reward.id,
    data: {
      ...reward
    }
  }))
})

const handleRowClick = (row: any) => {
  if (row && row.data) {
    navigateToRewardDetail(row.data)
  }
}

const handlePageChange = async (page: number) => {
  isRefreshing.value = true
  await loadRewards(null, 'created_at', 'DESC', page)
  isRefreshing.value = false
}

onMounted(async () => {
  isLoading.value = true
  await loadRewards(null, 'created_at', 'DESC', 1)
  isLoading.value = false
})
</script>

<template>
  <div v-if="!isLoading">
    <div class="flex flex-col">
      <TheListHeader
        :title="pageTitle?.title || ''"
        :count="{ filtered: pagination.total, total: pagination.total }"
        unit-key="global.words.reward"
      />

      <div v-if="hasRewards" class="flex flex-1 flex-col">
        <div v-if="activeRewards && activeRewards.length > 0" class="flex flex-1 flex-col px-6 sm:px-12">
          <AppTable
            :columns="tableColumns"
            :rows="tableRows"
            :is-loading="isRefreshing"
            :pagination="pagination.lastPage > 1 ? pagination : null"
            row-classes="cursor-pointer"
            @row-clicked="handleRowClick"
            @update:change-page="handlePageChange"
          >
            <template #td-image="{ data }">
              <div>
                <img
                  v-if="data?.pictureUrl"
                  :src="data.pictureUrl"
                  :alt="data.name || 'Reward image'"
                  class="h-10 w-16 min-w-16 rounded-lg object-cover object-center"
                />
                <div v-else class="h-10 w-16 min-w-16 rounded-lg border flex items-center justify-center">
                  <BaseIcon name="HeroGiftIconOutline" class="w-5 h-5 text-muted-foreground" />
                </div>
              </div>
            </template>

            <template #td-type="{ data }">
              <RewardTypeTag :reward-type="data?.rewardType" />
            </template>

            <template #td-name="{ data }">
              <div class="text-sm font-medium text-foreground">
                {{ data?.name || '-' }}
              </div>
            </template>

            <template #td-usage="{ data }">
              <RewardUsageTag :usage-tags="data?.usageTags || []" />
            </template>
          </AppTable>
        </div>
      </div>

      <div v-else class="flex flex-1 flex-col">
        <div class="px-6 sm:px-12">
          <Card>
            <CardContent class="flex flex-col items-center justify-center gap-6">
              <div
                class="flex flex-col items-center justify-center bg-muted rounded-xl border border-border py-7 px-4 w-full gap-2"
              >
                <div>
                  <BaseIcon name="HeroGiftIconSolid" class="w-8 h-8 text-foreground-disabled mx-auto" />
                </div>

                <div>
                  <h3 class="text-foreground text-center">
                    {{ t('pages.reward.library.emptyState.description1') }}
                  </h3>
                  <p class="text-foreground text-center">
                    {{ t('pages.reward.library.emptyState.description2') }}
                  </p>
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 w-full">
                <EmptyStateCard
                  icon="HeroHeartIconOutline"
                  :title="t('pages.reward.library.emptyState.blocks.loyalty.title')"
                  :description="t('pages.reward.library.emptyState.blocks.loyalty.description')"
                  :button-text="t('pages.reward.library.emptyState.blocks.loyalty.cta')"
                  :button-link="{ name: 'LoyaltyEdit', query: { openModal: 'true' } }"
                  color="orange"
                  :disabled="!canCreateReward"
                />

                <EmptyStateCard
                  icon="HeroStarIconOutline"
                  :title="t('pages.reward.library.emptyState.blocks.contest.title')"
                  :description="t('pages.reward.library.emptyState.blocks.contest.description')"
                  :button-text="t('pages.reward.library.emptyState.blocks.contest.cta')"
                  :button-link="{ name: 'ContestCreate' }"
                  color="yellow"
                  :disabled="!canCreateReward"
                />

                <EmptyStateCard
                  icon="HeroChatBubbleLeftEllipsisIconOutline"
                  :title="t('pages.reward.library.emptyState.blocks.smsCampaign.title')"
                  :description="t('pages.reward.library.emptyState.blocks.smsCampaign.description')"
                  :button-text="t('pages.reward.library.emptyState.blocks.smsCampaign.cta')"
                  :button-link="{ name: 'CampaignCreate', query: { type: 'sms' } }"
                  color="green"
                  :disabled="!canCreateReward"
                />

                <EmptyStateCard
                  icon="HeroBellIconOutline"
                  :title="t('pages.reward.library.emptyState.blocks.pushCampaign.title')"
                  :description="t('pages.reward.library.emptyState.blocks.pushCampaign.description')"
                  :button-text="t('pages.reward.library.emptyState.blocks.pushCampaign.cta')"
                  :button-link="{ name: 'CampaignCreate', query: { type: 'push' } }"
                  color="blue"
                  :disabled="!canCreateReward"
                />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div class="h-6 sm:h-8"></div>
    </div>
  </div>

  <TheLoadingPage v-else />
</template>
